import { useContext, useEffect } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useModal from '../../hooks/useModal';
import CircledIcon from '../../components/CircledIcon';
import Label from '../../components/Label/Label';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import {
  AreDashboardProvider,
  AreDashboardStore,
} from '../../providers/areDashboard';
import {
  ACCOUNT_ACTIVITY_ROUTE,
  OPEN_ENROLLMENT_ROUTE,
  HEALTH_SUMMARY,
  LIFE_EVENTS_ROUTE,
  PENSION_PROFILE_ROUTE,
  PROFILE_ROUTE,
  SAVINGS_SUMMARY,
} from '../../routes/types';
import ChangeOE_Modal from '../widgets/OpenEnrollment/ChangeOE_Modal';
import DecisionToolsModal from '../widgets/OpenEnrollment/DecisionTools';
import BenefitsLibraryModal from '../widgets/OpenEnrollment/BenefitsLibrary';
import BenefitOptionsCostsModal from '../widgets/OpenEnrollment/BenefitOptionsCosts';
import ExternalSiteWarningModal from '../widgets/OpenEnrollment/ExternalSiteWarning';

const today = new Date();

export type OpenEnrollmentHQViewProps = React.PropsWithChildren<{
  onClick: () => void;
}>;

const OpenEnrollmentHQView = ({ onClick }: OpenEnrollmentHQViewProps) => {
  const {
    fingertipLinks,
    companiesMessages,
    savingsSnapshot,
    coveredFamilyMembers,
    otherCompanyBenefits,
  } = useContext(AreDashboardStore) || {};

  const navigate = useNavigate();
  const [
    showChangeOE_Modal,
    handleShowChangeOE_Modal,
    handleCloseChangeOE_Modal,
  ] = useModal();

  const [showDecisionTools, handleShowDecisionTools, handleCloseDecisionTools] =
    useModal();
  useEffect(() => {
    handleShowDecisionTools();
  }, []);

  const [
    showBenefitsLibrary,
    handleShowBenefitsLibrary,
    handleCloseBenefitsLibrary,
  ] = useModal();

  const [
    showBenefitOptionsCosts,
    handleShowBenefitOptionsCosts,
    handleCloseBenefitOptionsCosts,
  ] = useModal();

  const [
    showExternalSiteWarning,
    handleShowExternalSiteWarning,
    handleCloseExternalSiteWarning,
  ] = useModal();

  return (
    <Container className={'cnd-custom-container-large mb-12 px-lg-9 px-xl-0'}>
      <Row>
        <div className="my-10 col-12">
          <div className="default-card blue one-column home_oe_banner">
            <div className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-column ">
                <div className="card-heading text-sm-20-lg-32 left"></div>
                <div className="card-subheading text-sm-12-lg-14"></div>
              </div>
            </div>
          </div>
        </div>
        <h4 className="h4 fw-light mb-0">Open Enrollment HQ</h4>
        <div className="fw-light text-fs-6 mt-4 mb-8">
          Explore details about Delta medical, prescription drug, dental, and
          vision coverage, as well as other tools and resources available to
          Delta employees and their dependents.
        </div>
      </Row>

      <Row>
        <Col xs={12} md={6} lg={3} xl={3} xxl={3}>
          <Card
            className="card promoAd h-100"
            onMouseDown={handleShowChangeOE_Modal}
          >
            <Card.Img
              variant="top"
              src={`${process.env.PUBLIC_URL}/${'adTop5.png'}`}
            />
            <Card.Body>
              <Card.Title className="mb-0 lh-xs fw-light">
                <h6 className="mb-0 text-sm-16-lg-20">
                  Changes to Know for OE
                </h6>
              </Card.Title>
              <Card.Text className="lh-xxs fw-light text-sub1 text-light-grey-normal">
                Your OE Highlights.
              </Card.Text>
            </Card.Body>
          </Card>
          <ChangeOE_Modal
            showChangeOE_Modal={showChangeOE_Modal}
            handleCloseChangeOE_Modal={handleCloseChangeOE_Modal}
          />
        </Col>

        <Col xs={12} md={6} lg={3} xl={3} xxl={3}>
          <Card
            className="card promoAd h-100"
            onMouseDown={handleShowDecisionTools}
          >
            <Card.Img
              variant="top"
              src={`${process.env.PUBLIC_URL}/${'adDecisionTools.png'}`}
            />
            <Card.Body>
              <Card.Title className="mb-0 lh-xs fw-light">
                <h6 className="mb-0 text-sm-16-lg-20">Decision Tools</h6>
              </Card.Title>
              <Card.Text className="lh-xxs fw-light text-sub1 text-light-grey-normal">
                Benefits Election Support
              </Card.Text>
            </Card.Body>
          </Card>

          <DecisionToolsModal
            showDecisionTools={showDecisionTools}
            handleCloseDecisionTools={handleCloseDecisionTools}
            handleShowExternalSiteWarning={handleShowExternalSiteWarning}
          />
          <ExternalSiteWarningModal
            showExternalSiteWarning={showExternalSiteWarning}
            handleCloseExternalSiteWarning={handleCloseExternalSiteWarning}
          />
        </Col>

        <Col xs={12} md={6} lg={3} xl={3} xxl={3}>
          <Card
            className="card promoAd h-100"
            onMouseDown={handleShowBenefitsLibrary}
          >
            <Card.Img
              variant="top"
              src={`${process.env.PUBLIC_URL}/${'adBenLibrary.png'}`}
            />
            <Card.Body>
              <Card.Title className="mb-0 lh-xs fw-light">
                <h6 className="mb-0 text-sm-16-lg-20">Benefits Library</h6>
              </Card.Title>
              <Card.Text className="lh-xxs fw-light text-sub1 text-light-grey-normal">
                Information on your benefit options
              </Card.Text>
            </Card.Body>
          </Card>

          <BenefitsLibraryModal
            showBenefitsLibrary={showBenefitsLibrary}
            handleCloseBenefitsLibrary={handleCloseBenefitsLibrary}
          />
        </Col>

        <Col xs={12} md={6} lg={3} xl={3} xxl={3}>
          <Card className="card promoAd h-100">
            <Card.Img
              variant="top"
              src={`${process.env.PUBLIC_URL}/${'adFAQ.png'}`}
            />
            <Card.Body>
              <Card.Title className="mb-0 lh-xs fw-light">
                <h6 className="mb-0 text-sm-16-lg-20">
                  Frequently Asked Questions
                </h6>
              </Card.Title>
              <Card.Text className="lh-xxs fw-light text-sub1 text-light-grey-normal">
                Answers to your most common questions
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="row row-cols-1 my-10 justify-content-md-center">
        <Col xs={12} md={12} lg={6} xl={6} xxl={6}>
          <Card className="card card-enrollment h-100">
            <Card.Body>
              <Card.Text>
                <div className="d-flex flex-row justify-content-between">
                  <div className="d-flex flex-column">
                    <div className="card-subheading">
                      <div className="px-0">
                        <div className="row">
                          <div
                            className="fw-bold primary missing"
                            data-resource-key="Open Enrollment"
                          >
                            Open Enrollment
                          </div>
                          <div className="lh-xxs fw-light text-sub1 text-light-grey-normal">
                            <span data-resource-key="Deadline">Deadline</span>:{' '}
                            <span>Nov 20, 2024 at 11:59 PM ET</span>
                          </div>
                          <div className="lh-xxs fw-light text-sub1 text-light-grey-normal">
                            <span data-resource-key="Status">Status</span>
                            <span>: </span>
                            <span data-resource-key="Enrollment Period">
                              Enrollment Period
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="button-holder pt-2 row">
                  <div className="col-8">
                    <a
                      href="#"
                      className="link-dark fs-10"
                      onMouseDown={handleShowBenefitOptionsCosts}
                    >
                      <CircledIcon
                        iconClassName="me-2"
                        variant="icon-white"
                        size={'icon-lg'}
                        icon={light('memo-circle-info')}
                      />
                      <span>Benefit Options &amp; Costs</span>
                    </a>
                    <BenefitOptionsCostsModal
                      showBenefitOptionsCosts={showBenefitOptionsCosts}
                      handleCloseBenefitOptionsCosts={
                        handleCloseBenefitOptionsCosts
                      }
                    />
                    
                  </div>
                  <div className="mx-auto col-4 text-end">
                    <button
                      onClick={() => navigate(OPEN_ENROLLMENT_ROUTE)}
                      type="button"
                      className="btn btn-primary btn-sm"
                      data-resource-key="Change"
                    >
                      Change
                    </button>
                  </div>
                </div>
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const OpenEnrollmentHQDashboard = () => {
  return (
    <AreDashboardProvider>
      <OpenEnrollmentHQView onClick={function (): void {
        throw new Error('Function not implemented.');
      } } />
    </AreDashboardProvider>
  );
};

export default OpenEnrollmentHQDashboard;
